import type { MandatoryItemPollingResponseDto, OptionalPollingKey } from '@mimir/common/dto/pollingDtos';
import type { ItemPollingProperty, ItemPollingResponse } from './itemPollingOps';
import type { ListenerInfo, ItemPollingPlan, ItemBookkeeping, ItemFullPollingResponse } from './itemPollingTypes';
import type { FetchedPeaks } from './peaksFetcherOps';
import { ItemState } from '@mimir/common/dto/itemState';
import { invariant } from '@mimir/common/util/invariant';

/**
 * Delay past which we will stop polling if we have not obtained any new peaks.
 */
const MAX_PEAK_POLLING_DELAY_IN_MS = 30000;


export function hasNewPeaks(oldPeaks: FetchedPeaks | undefined, newPeaks: FetchedPeaks): boolean {
  if (oldPeaks === undefined) {
    return true;
  }

  if (oldPeaks.peaksPerChannel.length === 0 || newPeaks.peaksPerChannel.length === 0) {
    return false;
  }

  return oldPeaks.peaksPerChannel[0]!.length !== newPeaks.peaksPerChannel[0]!.length;
}


/**
 * We want to aggregate for each item all the keys that we need in order
 * to satisfy all the poll listeners for this item. We also want to know
 * whether we should try to fetch peaks for this item.
 */
export function getItemPollingPlan(
  listeners: ListenerInfo<any>[],
  state: {
    hasPeakInfo: boolean,
    peaksComplete: boolean,
    hasAudioProxyUrls: boolean,
  },
): ItemPollingPlan {
  const allProperties = new Set<ItemPollingProperty>();
  for (const l of listeners) {
    l.requestedProperties.forEach(k => allProperties.add(k));
  }

  const allRequestedProperties = [...allProperties];
  const keys: OptionalPollingKey[] = [];

  let shouldFetchPeaks = false;

  for (const requestedProperty of allRequestedProperties) {
    switch (requestedProperty) {
      case 'playableDuration':
      case 'title':
      case 'aspectRatio':
      case 'segmentedMedia':
      case 'streaming': {
        // These properties are always part of the poll response
        break;
      }

      case 'peaks': {
        if (!state.hasPeakInfo) {
          // In order to start fetching peaks, we need the peakInfo property in order
          // to construct a peak fetcher object. Once we have it, we don't need to
          // ask the backend again for the peakInfo property
          keys.push('peakInfo');
        } else if (!state.peaksComplete) {
          // If a caller wants peaks, we have the peakInfo and we know that there may
          // be more peaks to fetch, then we can fetch peaks
          shouldFetchPeaks = true;
        }
        break;
      }

      case 'proxyAudioTrackUrls': {
        if (!state.hasAudioProxyUrls) {
          keys.push('proxyAudioTrackUrls');
        }
        break;
      }

      case 'mediaAvailability': {
        keys.push('mediaAvailability');
        break;
      }

      default: requestedProperty satisfies never; throw new Error(`Unsupported property: ${requestedProperty}`);
    }
  }

  // Let's sort the keys in order to have a deterministic output
  keys.sort();

  return {
    keys,
    shouldFetchPeaks,
  };
}


export type ItemPollingResponseLight = {
  itemPollingResponse: null | Pick<MandatoryItemPollingResponseDto, 'itemState' | 'streaming' | 'liveRecordingState'>,
  fetchedPeaks: null | Pick<FetchedPeaks, 'complete'>,
};

export function itemNeedsMorePolling(
  itemPollingResponse: ItemPollingResponseLight,
  now: number,
  bookkeeping: Pick<ItemBookkeeping, 'hasBeenStreaming' | 'lastNewPeaks'>,
): boolean {
  const pollResponseDto = itemPollingResponse.itemPollingResponse;
  if (pollResponseDto === null) {
    // If the item is not found, there is no point in polling anymore
    return false;
  }

  if (pollResponseDto.streaming === true) {
    bookkeeping.hasBeenStreaming = true;
  }

  if (itemPollingResponse.fetchedPeaks !== null) {
    if (itemPollingResponse.fetchedPeaks.complete) {
      // Fetching peaks returns a true complete value only when peaks have been finalized,
      // which is a clear indication that the item is not growing anymore
      return false;
    }

    // If we have obtained peaks recently, it means that there is still data to be obtained,
    // so we don't want to stop polling yet even if the item is reported as complete.
    // This may happen for instance with Kelda live folder ingests where the segments
    // are produced quickly and the item may be marked as complete before the backend
    // had time to process all the segments to create the peaks
    const recentPeaks = bookkeeping.lastNewPeaks !== undefined && (now - bookkeeping.lastNewPeaks) < MAX_PEAK_POLLING_DELAY_IN_MS;
    if (recentPeaks) {
      return true;
    }
  }

  if (pollResponseDto.streaming === false && (bookkeeping.hasBeenStreaming || ['resources_stopped', 'resources_deleted'].includes(pollResponseDto.liveRecordingState ?? ''))) {
    // If we see a false streaming status after having seen a true status or
    // if the cloud live state is already 'resources_stopped' or 'resources_deleted',
    // then we know that we can stop polling
    return false;
  }

  if (pollResponseDto.itemState === ItemState.COMPLETE) {
    // If we have no indication that the item was live/growing and
    // the item state says it's complete, let's stop polling
    return false;
  }

  // All cases in which we should stop polling are taken care of by one of the previous
  // conditions, so in any other case we keep polling
  return true;
}


export function buildItemPollingResponse<K extends keyof ItemPollingResponse>(
  itemId: string,
  requestedProperties: K[],
  itemPollingResponse: ItemFullPollingResponse,
  bookkeeping: Pick<ItemBookkeeping, 'peakInfo' | 'proxyAudioTrackUrls'>,
): null | Pick<ItemPollingResponse, K> {
  const pollResponseDto = itemPollingResponse.itemPollingResponse;

  if (pollResponseDto === null) {
    return null;
  }

  const result = {} as Partial<ItemPollingResponse>;
  for (const prop of requestedProperties) {

    switch (prop) {
      case 'playableDuration': {
        const playableDuration = pollResponseDto.playableDuration;
        invariant(playableDuration !== undefined, `Missing playableDuration property for item ${itemId}`);
        result.playableDuration = playableDuration;
        break;
      }

      case 'peaks': {
        const fetchedPeaks = itemPollingResponse.fetchedPeaks;
        if (fetchedPeaks !== null) {
          invariant(bookkeeping.peakInfo !== undefined, `Missing peak info for item ${itemId}`);
          result.peaks = {
            nPeaksPerSecondPerChannel: bookkeeping.peakInfo.nPeaksPerSecondPerChannel,
            peaksPerChannel: fetchedPeaks.peaksPerChannel,
          };
        } else {
          result.peaks = null;
        }
        break;
      }

      case 'proxyAudioTrackUrls': {
        result.proxyAudioTrackUrls = bookkeeping.proxyAudioTrackUrls ?? null;
        break;
      }

      case 'title': {
        result.title = pollResponseDto.title;
        break;
      }

      case 'aspectRatio': {
        result.aspectRatio = pollResponseDto.aspectRatio ?? null;
        break;
      }

      case 'segmentedMedia': {
        result.segmentedMedia = pollResponseDto.segmentedMedia;
        break;
      }

      case 'streaming': {
        result.streaming = pollResponseDto.streaming === true;
        break;
      }

      case 'mediaAvailability': {
        result.mediaAvailability = pollResponseDto.mediaAvailability;
        break;
      }

      default: prop satisfies never; throw new Error(`Unsupported property ${prop}`);
    }
  }

  // We know that our Partial<ItemPollingResponse> object contains all
  // the requested properties, so this cast is safe
  return result as Pick<ItemPollingResponse, K>;
}
